/** Auth service calls. */

import { api } from './api';
import type { CurrentUser, SsoProvider, TokenResponse } from '@/types';

export async function fetchSsoProviders(): Promise<SsoProvider[]> {
  const { data } = await api.get<SsoProvider[]>('/api/v1/auth/sso/list');
  return data;
}

/**
 * Get the IdP authorization URL and hand it back for a full-page navigation.
 *
 * `return_to` is always `<our origin>/sso-callback`. A 400 here means the app's registered
 * `url` in Bedrock doesn't match this origin exactly (scheme + host + port) — the response
 * `detail` says which, and our backend adds a clearer message for the local mismatch case.
 */
export async function startSsoLogin(providerId: string): Promise<string> {
  const returnTo = `${window.location.origin}/sso-callback`;
  const { data } = await api.get<{ redirect_url: string }>(
    `/api/v1/auth/sso/${encodeURIComponent(providerId)}/init`,
    { params: { return_to: returnTo } },
  );
  return data.redirect_url;
}

/** Break-glass local-admin login. The backend decides which auth path the credentials take. */
export async function loginWithPassword(
  username: string,
  password: string,
): Promise<TokenResponse> {
  const { data } = await api.post<TokenResponse>('/api/v1/auth/login', { username, password });
  return data;
}

export async function fetchCurrentUser(): Promise<CurrentUser> {
  const { data } = await api.get<CurrentUser>('/api/v1/auth/me');
  return data;
}

export async function logout(): Promise<void> {
  await api.post('/api/v1/auth/logout');
}

/** Force a re-verify against Bedrock, bypassing the permission cache. */
export async function refreshPermissions(): Promise<CurrentUser> {
  const { data } = await api.post<CurrentUser>('/api/v1/auth/permissions/refresh');
  return data;
}
